import * as angular from 'angular';
import * as $ from 'jquery';
import * as SimpleMDE from 'simplemde';

import { ComponentDefinition } from '../../models';

class ModernMarkdownEditorController implements angular.IComponentController, angular.IPostLink {
    isLoading: boolean;
    loadingMessage: string;
    model: string;
    options: SimpleMDE.Options;
    form: angular.IFormController;
    applicationGuid: string;
    modal: {
        directory: string;
        label: string;
        onOk: (fileName: string) => void;
        extensions?: string;
    };
    hasVariablesHidden: boolean;

    readonly onChange: () => void;

    private _cm: CodeMirror.Editor;

    static $inject = [
        '$scope',
        '$element',
        '$translate'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$element: angular.IAugmentedJQuery,
        private _$translate: angular.translate.ITranslateService
    ) {
    }

    $onInit() {
        const toolbar = [
            // Recreate default buttons for translations. See simplemde.js::1069
            {
                name: 'bold',
                action: SimpleMDE.toggleBold,
                className: 'fa fa-bold',
                title: this._$translate.instant('Bold')
            },
            {
                name: 'italic',
                action: SimpleMDE.toggleItalic,
                className: 'fa fa-italic',
                title: this._$translate.instant('Italic')
            },
            {
                name: 'strikethrough',
                action: SimpleMDE.toggleStrikethrough,
                className: 'fa fa-strikethrough',
                title: this._$translate.instant('Strikethrough')
            },
            {
                name: 'heading-smaller',
                action: SimpleMDE.toggleHeadingSmaller,
                className: 'fa fa-header fa-header-x fa-header-smaller',
                title: this._$translate.instant('Smaller Heading')
            },
            {
                name: 'heading-bigger',
                action: SimpleMDE.toggleHeadingBigger,
                className: 'fa fa-header fa-header-x fa-header-bigger',
                title: this._$translate.instant('Bigger Heading')
            },
            '|',
            {
                name: 'unordered-list',
                action: SimpleMDE.toggleUnorderedList,
                className: 'fa fa-list-ul',
                title: this._$translate.instant('Bulleted list')
            },
            {
                name: 'ordered-list',
                action: SimpleMDE.toggleOrderedList,
                className: 'fa fa-list-ol',
                title: this._$translate.instant('Numbered list')
            },
            {
                name: 'code',
                action: SimpleMDE.toggleCodeBlock,
                className: 'fa fa-code',
                title: this._$translate.instant('Code')
            },
            {
                name: 'quote',
                action: SimpleMDE.toggleBlockquote,
                className: 'fa fa-quote-left',
                title: this._$translate.instant('Blockquote')
            },
            {
                action: editor => {
                    this._cm = editor.codemirror;
                    this.modal = {
                        directory: 'Images',
                        label: 'Image',
                        onOk: this.onImageOk.bind(this)
                    };
                    if (!this._$scope.$$phase) {
                        this._$scope.$apply();
                    }
                    $('#mde-upload-file').modal('show');
                },
                className: 'fa fa-image',
                name: 'Image',
                title: this._$translate.instant('Image')
            },
            {
                action: editor => {
                    this._cm = editor.codemirror;
                    this.modal = {
                        directory: 'Images/Documentation',
                        label: 'Link to file',
                        onOk: this.onFileOk.bind(this),
                        extensions: '*'
                    };
                    if (!this._$scope.$$phase) {
                        this._$scope.$apply();
                    }
                    $('#mde-upload-file').modal('show');
                },
                className: 'fa fa-link',
                name: 'File',
                title: this._$translate.instant('Link to file')
            }
        ];

        if (!this.hasVariablesHidden) {
            toolbar.push({
                action: editor => {
                    this._cm = editor.codemirror;
                },
                className: 'fa fa-list-alt',
                name: 'Variable',
                title: this._$translate.instant('Variable')
            });
        }
        this.options = {
            autofocus: false,
            autosave: {
                enabled: false,
                uniqueId: 'simple-markdown-editor'
            },
            element: document.getElementById('simple-markdown-editor'),
            forceSync: false,
            hideIcons: ['guide', 'fullscreen', 'preview', 'side-by-side'],
            indentWithTabs: false,
            // initialValue: this.model,
            lineWrapping: true,
            placeholder: '',
            promptURLs: true,
            spellChecker: false,
            status: false,
            styleSelectedText: true,
            tabSize: 2,
            toolbar
        };
    }

    $postLink(): void {
        if (!this.hasVariablesHidden) {
            // Horrendous hack to render variables dropdown
            const variables = [
                '{ApplicationName}',
                '{GroupName}',
                '{GroupUniqueName}',
                '{GroupShortComment}',
                '{GroupLongComment}',
                '{GroupDocumentation}',
                '{Userfield:FieldName}',
                '{BeginDate}',
                '{EndDate}',
                '{PrintDate}',
                '{ApprovalDate}',
                '{RevisionNumber}',
                '{FileName}',
            ];
            const dropdown = $('<ul></ul>')
                .attr('id', 'mde-variables')
                .addClass('dropdown-menu');
            variables.forEach((v, i) => {
                const newLi = $('<li><span>' + v + '</span></li>')
                    .on('click', () => {
                        if (this._cm) {
                            this.replaceSelection(this._cm, v);
                        }
                    });
                dropdown.append(newLi);
            });
            const toolbar = this._$element.find('div.editor-toolbar');
            const anchorElement = toolbar.find('.fa.fa-list-alt');
            anchorElement.attr('data-toggle', 'dropdown');
            const container = $('<div></div>')
                .addClass('dropdown')
                .css('display', 'inline-block');
            container
                .append(anchorElement)
                .append(dropdown);
            toolbar.append(container);
        }
    }

    onImageOk(fileName: string) {
        if (this._cm) {
            this.createImageLink(this._cm, fileName);
            this._cm = null;
        }
    }

    onFileOk(fileName: string) {
        if (this._cm) {
            this.createFileLink(this._cm, fileName);
            this._cm = null;
        }
    }

    onTextChange() {
        if (this.onChange) {
            this.onChange();
        }
    }

    private createImageLink(editor: CodeMirror.Editor, file: string) {
        if (file) {
            file = encodeURI(file);
            this.replaceSelection(editor, `![${file}](${file})`);
        }
    }

    private createFileLink(editor: CodeMirror.Editor, file: string) {
        if (file) {
            file = encodeURI(file);
            this.replaceSelection(editor, `[${file}](${file})`);
        }
    }

    private replaceSelection(editor: CodeMirror.Editor, text: string) {
        // Very basic implementation of insert at position
        editor.replaceSelection(text);
    }
}

export const wtModernMarkdownEditor: ComponentDefinition = {
    componentId: 'wtModernMarkdownEditor',
    controller: ModernMarkdownEditorController,
    template: require('./modernMarkdownEditor.component.html'),
    controllerAs: 'vm',
    bindings: {
        model: '=',
        theme: '<',
        applicationGuid: '<?',
        hasVariablesHidden: '<?',
        onChange: '&?'
    }
};